import { Link } from 'react-router-dom';

export interface ContentItem {
  icon: string;
  title: string;
  desc: string;
  link?: string;
}

interface Props {
  label: string;
  title: string;
  subtitle?: string;
  items: ContentItem[];
  layout?: 'featured' | 'grid2' | 'grid3';
  dark?: boolean;
}

export default function SectionContent({ label, title, subtitle, items, layout = 'grid3', dark = false }: Props) {
  const bg = dark ? 'bg-[#050a12]' : 'bg-white';
  const textPrimary = dark ? 'text-white' : 'text-[#0c1520]';
  const textSub = dark ? 'text-white/50' : 'text-[#64748b]';
  const cardBg = dark ? 'bg-white/4 border-white/8' : 'bg-[#f8f7f2] border-transparent';
  const cardHover = dark ? 'hover:border-[#c9a84c]/40 hover:bg-white/6' : 'hover:border-[#c9a84c]/50 hover:shadow-lg hover:shadow-[#c9a84c]/5';
  const iconBg = dark ? 'bg-[#c9a84c]/15 border-[#c9a84c]/20' : 'bg-[#050a12]';

  return (
    <section className={`${bg} py-24 relative overflow-hidden`}>
      {/* Subtle background pattern */}
      {!dark && (
        <div className="absolute inset-0 opacity-[0.015]" style={{ backgroundImage: 'repeating-linear-gradient(0deg, #000 0, #000 1px, transparent 0, transparent 50%), repeating-linear-gradient(90deg, #000 0, #000 1px, transparent 0, transparent 50%)', backgroundSize: '40px 40px' }} />
      )}
      <div className="relative z-10 max-w-7xl mx-auto px-6">
        {/* Section header */}
        <div className="max-w-2xl mb-16">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-[2px] bg-[#c9a84c]" />
            <span className="text-[#c9a84c] text-[11px] font-bold uppercase tracking-[0.28em]">{label}</span>
          </div>
          <h2
            className={`font-bold ${textPrimary} leading-[1.1] mb-5`}
            style={{ fontFamily: "'Playfair Display', serif", fontSize: 'clamp(32px, 4vw, 52px)' }}
          >
            {title}
          </h2>
          <div className="flex items-center gap-2.5 mb-5">
            <div className="w-12 h-[3px] bg-[#c9a84c] rounded-full" />
            <div className="w-3 h-[3px] bg-[#c9a84c]/35 rounded-full" />
          </div>
          {subtitle && <p className={`${textSub} text-lg leading-relaxed`}>{subtitle}</p>}
        </div>

        {/* Featured layout */}
        {layout === 'featured' && (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-5">
              {items.slice(0, 2).map((item) => (
                <div key={item.title} className="group relative bg-[#050a12] border border-white/8 rounded-2xl p-8 flex flex-col hover:border-[#c9a84c]/35 hover:-translate-y-1 transition-all duration-300 overflow-hidden">
                  {/* Card glow */}
                  <div className="absolute top-0 right-0 w-40 h-40 bg-[#c9a84c]/5 rounded-full blur-3xl opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                  <div className="relative z-10">
                    <div className="w-13 h-13 w-12 h-12 flex items-center justify-center bg-[#c9a84c]/15 border border-[#c9a84c]/20 rounded-xl mb-6">
                      <i className={`${item.icon} text-[#c9a84c] text-xl`} />
                    </div>
                    <h3 className="text-white font-bold text-2xl mb-3 leading-snug" style={{ fontFamily: "'Playfair Display', serif" }}>
                      {item.title}
                    </h3>
                    <p className="text-white/55 text-base leading-relaxed flex-1 mb-6">{item.desc}</p>
                    {item.link && (
                      <Link to={item.link} className="inline-flex items-center gap-2 text-[#c9a84c] text-sm font-bold hover:gap-3 transition-all duration-200 cursor-pointer group/link">
                        Learn More
                        <div className="w-4 h-4 flex items-center justify-center">
                          <i className="ri-arrow-right-line text-sm group-hover/link:translate-x-1 transition-transform" />
                        </div>
                      </Link>
                    )}
                  </div>
                </div>
              ))}
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {items.slice(2).map((item) => (
                <div key={item.title} className={`group relative border ${cardBg} ${cardHover} rounded-xl p-6 flex flex-col hover:-translate-y-1 transition-all duration-300`}>
                  <div className={`w-11 h-11 flex items-center justify-center ${iconBg} rounded-xl mb-5 flex-shrink-0`}>
                    <i className={`${item.icon} text-[#c9a84c] text-lg`} />
                  </div>
                  <h3 className={`${textPrimary} font-bold text-lg mb-2 leading-snug`} style={{ fontFamily: "'Playfair Display', serif" }}>
                    {item.title}
                  </h3>
                  <p className={`${textSub} text-sm leading-relaxed flex-1 mb-4`}>{item.desc}</p>
                  {item.link && (
                    <Link to={item.link} className="inline-flex items-center gap-1.5 text-[#c9a84c] text-xs font-bold cursor-pointer hover:gap-2.5 transition-all">
                      Learn More <i className="ri-arrow-right-line text-xs" />
                    </Link>
                  )}
                </div>
              ))}
            </div>
          </>
        )}

        {/* Grid 2 */}
        {layout === 'grid2' && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {items.map((item) => (
              <div key={item.title} className={`group flex items-start gap-5 border ${cardBg} ${cardHover} rounded-xl p-7 hover:-translate-y-1 transition-all duration-300`}>
                <div className={`w-12 h-12 flex items-center justify-center ${iconBg} rounded-xl flex-shrink-0`}>
                  <i className={`${item.icon} text-[#c9a84c] text-xl`} />
                </div>
                <div>
                  <h3 className={`${textPrimary} font-bold text-xl mb-2 leading-snug`} style={{ fontFamily: "'Playfair Display', serif" }}>
                    {item.title}
                  </h3>
                  <p className={`${textSub} text-sm leading-relaxed mb-3`}>{item.desc}</p>
                  {item.link && (
                    <Link to={item.link} className="inline-flex items-center gap-1.5 text-[#c9a84c] text-xs font-bold cursor-pointer hover:gap-2.5 transition-all">
                      Learn More <i className="ri-arrow-right-line text-xs" />
                    </Link>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Grid 3 */}
        {layout === 'grid3' && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {items.map((item, idx) => (
              <div key={item.title} className={`group relative border ${cardBg} ${cardHover} rounded-xl p-7 hover:-translate-y-1 transition-all duration-300 overflow-hidden`}>
                {/* Number watermark */}
                <span
                  className={`absolute top-3 right-4 text-6xl font-black leading-none select-none ${dark ? 'text-white/4' : 'text-[#c9a84c]/8'}`}
                  style={{ fontFamily: "'Playfair Display', serif" }}
                >
                  {String(idx + 1).padStart(2, '0')}
                </span>
                <div className={`relative z-10 w-12 h-12 flex items-center justify-center ${iconBg} rounded-xl mb-5`}>
                  <i className={`${item.icon} text-[#c9a84c] text-xl`} />
                </div>
                <h3 className={`relative z-10 ${textPrimary} font-bold text-xl mb-2.5 leading-snug`} style={{ fontFamily: "'Playfair Display', serif" }}>
                  {item.title}
                </h3>
                <p className={`relative z-10 ${textSub} text-sm leading-relaxed`}>{item.desc}</p>
                {item.link && (
                  <Link to={item.link} className="relative z-10 inline-flex items-center gap-1.5 text-[#c9a84c] text-xs font-bold cursor-pointer mt-4 hover:gap-2.5 transition-all">
                    Learn More <i className="ri-arrow-right-line text-xs" />
                  </Link>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}
